import {
  fieldSalesRepInfo,
  fieldIsVulcanOrder,
  fieldNewWirelineService,
  fieldProvinceVUL,
  vulcanProvinces,
  fieldStoreCodeVulcan,
  fieldStoreCodeOutletVulcan,
  storeCodes,
  storeCodeOutletMapping,
  prequalificationField,
  prequalificationSection,
  errorNotePrequalificationField,
  tsb15SectionKeys
} from '../../utils/constant';
import { updatePrequalificationField } from '../vulcanHelpers/tsbSection/utils';
import { makeBusinessOptikTvRule } from './rules/businessOptikTv';
import { makeFilterProductRule } from './rules/filterProduct';
import { makeSecureBusinessRule } from './rules/secureBusiness';
import { makeFibreBusinessWifiRule } from './rules/fibreBusinessWifi';
import { makeFibreOffersRule } from './rules/fibreOffers';
import {
  makeCustomerProductRule,
  makePrequalificationResetRule
} from './rules/customerProduct';
import { makeInlineFieldRules } from './rules/inlineFieldRules';

/**
 * PURE rewrite of Vulcan's per-field helpers as `@mobilelive-inc/telus-forms-core`
 * `fieldRules` — the design decision recorded in memory
 * (forms-core-fieldrules-pure-rewrite): each rule is a pure
 * `(field, state, values) => patch | undefined` over the engine's FLAT `values`
 * bag + field definition, NOT the app's rich `formFields` record. This is what
 * lets Stage 4 delete `normalizedFields`.
 *
 * The engine calls `fieldRules(field, state, values)` and merges the returned
 * patch onto the field's generic state (`visibility` / `options` / `disabled` /
 * `value` override; other keys merge). Extra context the old helpers read from
 * closures/props — `orderData`, `channelName`, `dealershipName`, `salesRepOutlet`,
 * `locale`, and the locale copy `t` (`vulcanWebform`) — is closed over by
 * `createVulcanFieldRules(context)`, since the engine passes only
 * `(field, state, values)`.
 *
 * Two mechanical translations recur:
 *  - `formFields[X].value`  → `values[X]`.
 *  - `fields.options = …`   → return `{ options: … }`.
 *  - `return true` (a helper hiding the field) → `{ visibility: false }`.
 *  - a cross-field write `formFields[OTHER].value = …` → a **self-derivation**
 *    on OTHER's own rule (`{ value: … }`), submitted via the engine's
 *    resolved-values payload (alpha.3). See `deriveStoreOutletCode`.
 *
 * Rules are composed (see `composeRules`): visibility is three-way (hard-hide
 * wins > force-show > declarative), and later rules see earlier rules' patches
 * via a threaded evolving state — matching the old sequential-mutation order.
 *
 * @typedef {Object} VulcanRuleContext
 * @property {Object} orderData
 * @property {string} [channelName]
 * @property {string} [dealershipName]
 * @property {string} [salesRepOutlet]
 * @property {string} locale
 * @property {Object} t   The active locale's `vulcanWebform` copy (EN/FR)
 * @property {Array} [secureBusinessPlanOptions]   The `secureBusinessPlanVulcan`
 *   field's own raw options (`{fields:{value,label,...}}[]`) — a field's own
 *   definition isn't visible from a DIFFERENT field's rule invocation
 *   (`fieldRules` only ever gets the field currently being evaluated), so this
 *   is threaded in explicitly for the one place that needs it cross-field: the
 *   "3 Months Free" offer's price derives from the plan's OWN option list.
 *

 * @callback VulcanRule
 * @param {Object} field
 * @param {Object} state
 * @param {Object.<string,*>} values
 * @returns {Object|undefined}
 */

const storeCodesOptions = () =>
  storeCodes.map(code => ({ fields: { label: code, value: code } }));

/* ---------------------------------------------------------------------------
 * Visibility / hide rules (helpers that `return true` to drop a field).
 * ------------------------------------------------------------------------- */

/** PrequalificationHelpers: the Sales Rep Info field is never shown here. */
const hideSalesRepInfo = field =>
  field.name === fieldSalesRepInfo ? { visibility: false } : undefined;

/**
 * CustomerHelpers' two hide branches:
 *  - `isVulcanOrder` question is only asked (and required) for Vulcan provinces.
 *  - `newWirelineService` is hidden for a Vulcan order.
 */
const customerSectionHides = ctx => (field, state, values) => {
  const serviceAddress = values[fieldProvinceVUL] || ctx.orderData.province_VUL;
  if (field.name === fieldIsVulcanOrder) {
    if (!vulcanProvinces.includes(serviceAddress)) {
      // was: value = undefined; isRequired = false; return true
      return { visibility: false, isRequired: false, value: undefined };
    }
    return { isRequired: true };
  }
  if (
    field.name === fieldNewWirelineService &&
    ctx.orderData.isThisVulcanOrder
  ) {
    return { visibility: false, isRequired: false };
  }
  return undefined;
};

/**
 * prequalificationSectionHelpers: hide the TSB-1.5 sub-sections, the official
 * note, and the prequalification error note based on the sales-rep outlet and
 * the "existing security equipment" answer.
 */
const SPECIAL_OUTLET_ID = '1100054384';
const prequalificationHides = ctx => (field, state, values) => {
  const { salesRepOutlet, t } = ctx;
  const { name } = field;
  const isSpecialOutlet = salesRepOutlet === SPECIAL_OUTLET_ID;
  const existingSecurityEquipmentIsNotYes =
    values.existingSecurityEquipment !== t.yes;

  if (
    tsb15SectionKeys.includes(name) &&
    (!isSpecialOutlet || existingSecurityEquipmentIsNotYes)
  ) {
    return { visibility: false };
  }
  if (name === 'officialNote' && !isSpecialOutlet) {
    return { visibility: false };
  }
  if (name === errorNotePrequalificationField) {
    const updated = updatePrequalificationField(
      salesRepOutlet,
      prequalificationField
    );
    const anyYes = updated.some(f => values[f] === t.yes);
    if (!anyYes) return { visibility: false };
  }
  if (name === prequalificationSection) {
    // Section-level marker field: no hide here (parity with the original, which
    // only recomputes the field list for the error-note branch above).
    return undefined;
  }
  return undefined;
};

/* ---------------------------------------------------------------------------
 * Cross-field derivations (old `formFields[OTHER].value = …` mutations, flipped
 * into self-derivations on the target field).
 * ------------------------------------------------------------------------- */

/**
 * StoreCodeOptionsHelper, split into two pure rules:
 *  - the store-code field gets its options list (when the definition has none);
 *  - the outlet-code field DERIVES its value from the selected store code via
 *    `storeCodeOutletMapping` (was a write into `formFields[outlet].value`).
 */
const storeCodeOptions = (field, state) => {
  if (field.name === fieldStoreCodeVulcan && !state.options) {
    return { options: storeCodesOptions() };
  }
  return undefined;
};
const deriveStoreOutletCode = (field, state, values) => {
  if (field.name === fieldStoreCodeOutletVulcan) {
    const mapped = storeCodeOutletMapping[values[fieldStoreCodeVulcan]];
    if (mapped) return { value: mapped };
  }
  return undefined;
};

/* ---------------------------------------------------------------------------
 * Composition.
 * ------------------------------------------------------------------------- */

/**
 * Compose an ordered list of rules into one `fieldRules`. Vulcan's helpers use
 * two visibility mechanisms, so combination is three-way, not a plain AND:
 *  - a rule returning `visibility: false` HARD-HIDES (mirrors a hide-helper's
 *    `return true`, which dropped the field entirely) — dominant;
 *  - a rule returning `visibility: true` FORCE-SHOWS (mirrors a helper's explicit
 *    `fields.isVisible = true`), overriding the declarative result — but never a
 *    hard-hide;
 *  - if no rule touches visibility, the declarative `state.isVisible` stands.
 * `options` / `value` / other scalar keys are last-write-wins, matching the
 * helpers' old sequential-mutation order.
 *
 * @param {VulcanRule[]} rules
 * @returns {VulcanRule}
 */
export function composeRules(rules) {
  return (field, state, values) => {
    // Thread an EVOLVING state through the rules so a later rule sees an earlier
    // rule's patch — mirroring the old helpers, which mutated a shared
    // `formFields` in sequence (e.g. CustomerHelpers filters addProduct.options,
    // then filterProduct filters again on top). Rules should read `state.options`
    // / `state.value` (the evolving values), not the raw `field` def.
    const work = { ...state };
    const touched = new Set();
    let hardHide = false;
    let forceShow = false;
    rules.forEach(rule => {
      const p = rule(field, work, values);
      if (!p) return;
      Object.keys(p).forEach(k => touched.add(k));
      if ('visibility' in p) {
        if (p.visibility === false) hardHide = true;
        else forceShow = true;
        work.isVisible = p.visibility;
      }
      if ('options' in p) work.options = p.options;
      if ('value' in p) work.value = p.value;
      if ('disabled' in p) work.disabled = p.disabled;
      const { visibility: _v, ...rest } = p;
      Object.assign(work, rest);
    });
    if (touched.size === 0) return undefined;
    /** @type {Object} */
    const patch = {};
    touched.forEach(k => {
      if (k === 'visibility') {
        // eslint-disable-next-line no-nested-ternary
        patch.visibility = hardHide
          ? false
          : forceShow
          ? true
          : state.isVisible;
      } else {
        patch[k] = work[k];
      }
    });
    return patch;
  };
}

/**
 * Build Vulcan's `fieldRules` closure. The remaining option-filter helpers
 * (CustomerHelpers product filters, filterProduct, SecureBusinessHelpers,
 * BusinessOptikTvHelpers, FibreBusinessWifiHelpers) are ported in the next
 * tranche and appended to this list.
 *
 * @param {VulcanRuleContext} context
 * @returns {VulcanRule}
 */
export function createVulcanFieldRules(context) {
  const ctx = context || {};
  return composeRules([
    hideSalesRepInfo,
    customerSectionHides(ctx),
    prequalificationHides(ctx),
    storeCodeOptions,
    deriveStoreOutletCode,
    makeBusinessOptikTvRule(ctx),
    makeSecureBusinessRule(ctx),
    makeFibreBusinessWifiRule(ctx),
    makeFibreOffersRule(ctx),
    makePrequalificationResetRule(ctx),
    // CustomerHelpers' addProduct filter runs BEFORE filterProduct, which then
    // filters on top of the customer-filtered options via the threaded state.
    makeCustomerProductRule(ctx),
    makeFilterProductRule(ctx),
    makeInlineFieldRules(ctx)
  ]);
}

export default createVulcanFieldRules;
